/**
 * POT GADGETS — small purchasable devices attached to an INDIVIDUAL pot (finer grained than the
 * per-shelf grow lights). Each is bought from the shop, then PLACED onto a specific pot (the player
 * picks the pot, like grow lights pick a shelf). Purchasable quantity is capped at the number of pots
 * the player currently has (one of each gadget per pot).
 *
 * First gadget: the GROWTH TIMER — a little digital clock on the pot's front face that shows the
 * time remaining until the plant in that pot matures.
 */

/** Price of the player's FIRST growth timer. Each one after it costs more (see `nextTimerPrice`). */
export const GROWTH_TIMER_PRICE = 45;
/** Player LEVEL required to buy the growth timer (after the power chain, since it draws power). */
export const GROWTH_TIMER_UNLOCK_LEVEL = 9;

/** Per-unit price growth for growth timers. Timers are the ONLY greenhouse fitting that goes to 60 units
 *  (one per pot), so they get a gentle FORMULA rather than a hand table: a table of 60 rows would be
 *  unreadable and a steep multiplier would be absurd by the end. 1.045 per unit ≈ ×1.6 every 10 timers,
 *  so #1 is 45, #30 is 160 and #60 is 605 — the last pot is a decision, not a wall. Rounded to 5 coins so
 *  every price in the shop reads cleanly. Full ladder in BALANCE.md Appendix C. */
const TIMER_PRICE_GROWTH = 1.045;
/** The cost of the NEXT growth timer given how many the player already owns. */
export function nextTimerPrice(owned: number): number {
  const n = Math.max(0, owned);
  return Math.round(GROWTH_TIMER_PRICE * Math.pow(TIMER_PRICE_GROWTH, n) / 5) * 5;
}

/** Shelves (tiers) per rack. One grow light and one sprinkler pair fit per SHELF, so this sets their cap. */
export const TIERS_PER_RACK = 3;
/** Pots per rack in the greenhouse (3 tiers × 4 pots). Used to cap purchasable pot gadgets. */
export const POTS_PER_RACK = 12;
/** Total shelves the player owns — the cap on grow lights and on sprinkler pairs alike. */
export function totalShelves(rackCount: number): number { return rackCount * TIERS_PER_RACK; }
/** Total pots the player owns (one timer max per pot). */
export function totalPots(rackCount: number): number { return rackCount * POTS_PER_RACK; }

// ---- SHELVES (racks) -------------------------------------------------------
/** The player STARTS with one shelf and can buy more (added to the right) up to this many. */
export const STARTING_RACKS = 1;
export const MAX_RACKS = 5;
/** Price (coins) of each additional shelf, indexed by which shelf it is (the 2nd shelf = index 0, …).
 *  Escalating so expansion stays meaningful as the garden grows. */
const SHELF_PRICES = [400, 900, 1800, 3200];   // for shelves #2, #3, #4, #5
/** GARDENING LEVEL required to unlock each additional shelf (#2..#5). Level-gated so a player can't buy their
 *  way to a full greenhouse before they've grown into it — expansion paces with gardening progress, not just
 *  coins (mirrors how plants & tackle gate on level). Coins are still required on top. */
const SHELF_UNLOCK_LEVELS = [3, 8, 14, 22];    // for shelves #2, #3, #4, #5
/** The cost to buy the NEXT shelf given the current count, or null if already at MAX_RACKS. */
export function nextShelfPrice(currentCount: number): number | null {
  if (currentCount >= MAX_RACKS) return null;
  return SHELF_PRICES[currentCount - STARTING_RACKS] ?? SHELF_PRICES[SHELF_PRICES.length - 1]!;
}
/** The GARDENING LEVEL required to buy the NEXT shelf given the current count (0 if none / already maxed). */
export function nextShelfUnlockLevel(currentCount: number): number {
  if (currentCount >= MAX_RACKS) return 0;
  return SHELF_UNLOCK_LEVELS[currentCount - STARTING_RACKS] ?? 0;
}
/** WHICH shelf (#2..#5) this exact gardening level opens the gate on, or null if no shelf unlocks there.
 *  The inverse of `nextShelfUnlockLevel`, for callers that have a level and want the shelf rather than a
 *  rack count and want the level — the level-up modal, which must not miss 3/8/14/22. Reads the same
 *  SHELF_UNLOCK_LEVELS table the purchase path gates on, so the two can never disagree. */
export function shelfUnlockedAt(level: number): number | null {
  const i = SHELF_UNLOCK_LEVELS.indexOf(level);
  return i < 0 ? null : i + STARTING_RACKS + 1;   // index 0 is shelf #2
}
/** The coin price of a named shelf (#2..#5) — the price column read by shelf NUMBER rather than by how many
 *  racks the player holds, so the level-up modal can quote it without knowing the player's rack count. */
export function shelfPriceFor(shelfNumber: number): number {
  return SHELF_PRICES[shelfNumber - STARTING_RACKS - 1] ?? SHELF_PRICES[SHELF_PRICES.length - 1]!;
}

/** Format seconds-remaining as a compact clock string for the pot display (e.g. "1:23:45", "4:07",
 *  "0:38"). Returns "READY" at/under zero. */
export function formatTimer(secondsLeft: number): string {
  if (secondsLeft <= 0) return "READY";
  const s = Math.ceil(secondsLeft);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(sec)}`;
  return `${m}:${pad(sec)}`;
}
